/**
 * Options for offsetting time from a date
 */
export interface OffsetDateOptions {
  years?: number
  months?: number
  weeks?: number
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
  milliseconds?: number
}

/**
 * Offsets a date by specified time units (forward or backward) and returns a new Date object.
 * @param date - The original Date object to offset.
 * @param options - An object specifying the time units to offset (positive to add, negative to subtract).
 * @returns A new Date object with the time offset applied.
 *
 * @example
 * ```ts
 * const date = new Date('2023-01-15')
 * const newDate = offsetDate(date, { months: 2, days: 5 })
 * // Result: 2023-03-20
 * ```
 *
 * @example
 * ```ts
 * const date = new Date('2023-06-15 10:30:00')
 * const newDate = offsetDate(date, { hours: -3, minutes: 45 })
 * // Result: 2023-06-15 07:45:00
 * ```
 */
export function offsetDate(date: Date, options: OffsetDateOptions): Date {
  // Create a copy of the original date to avoid mutating it
  const result = new Date(date.getTime())

  // Add years and months first (they affect the calendar)
  if (options.years) {
    result.setFullYear(result.getFullYear() + options.years)
  }

  if (options.months) {
    const currentMonth = result.getMonth()
    const currentYear = result.getFullYear()
    const newMonth = currentMonth + options.months
    const newYear = currentYear + Math.floor(newMonth / 12)

    result.setMonth(newMonth % 12)
    result.setFullYear(newYear)

    // Handle the case where adding months changes the day
    // Adjust days if necessary to ensure the date is valid
    const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()
    if (result.getDate() > lastDayOfMonth) {
      result.setDate(lastDayOfMonth)
    }
  }

  // Add time-based units (convert everything to milliseconds)
  let millisecondsToAdd = 0

  if (options.weeks) {
    millisecondsToAdd += options.weeks * 7 * 24 * 60 * 60 * 1000
  }

  if (options.days) {
    millisecondsToAdd += options.days * 24 * 60 * 60 * 1000
  }

  if (options.hours) {
    millisecondsToAdd += options.hours * 60 * 60 * 1000
  }

  if (options.minutes) {
    millisecondsToAdd += options.minutes * 60 * 1000
  }

  if (options.seconds) {
    millisecondsToAdd += options.seconds * 1000
  }

  if (options.milliseconds) {
    millisecondsToAdd += options.milliseconds
  }

  // Apply all time-based additions at once
  if (millisecondsToAdd !== 0) {
    result.setTime(result.getTime() + millisecondsToAdd)
  }

  return result
}
